import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, styled } from "@mui/material";
import React from "react";
import FilterListItem from "../FilterListItem";

export interface MonthRangeFilterProps {
  title: string;
  name?: string;
  tooltip?: string;
  startDate?: number;
  endDate?: number;
  value: (number | null)[];
  disabled?: boolean;
  open?: boolean;
  setOpen: (open: boolean) => void;
  onChange: (value: (number | null)[]) => void;
  noWrap?: boolean;
  /** First year in the year dropdowns and default start when range is cleared. Default 2010. */
  minYear?: number;
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: "140px",
}));

const MonthRangeFilter: React.FC<MonthRangeFilterProps> = ({
  title = "Date Range Filter",
  name = "date-range-filter",
  tooltip,
  startDate = 0,
  endDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime(),
  value = [null, null],
  disabled,
  open,
  setOpen,
  onChange,
  noWrap = false,
  minYear = 2010,
}) => {
  const [fromDate, toDate] = value;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const getYearMonth = (timestamp: number | undefined, fallbackYear: number, fallbackMonth: number) => {
    if (typeof timestamp !== "number" || timestamp <= 0) {
      return { year: fallbackYear, month: fallbackMonth };
    }

    const parsed = new Date(timestamp);
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;

    return {
      year: Number.isFinite(year) && year >= minYear && year <= currentYear ? year : fallbackYear,
      month: Number.isFinite(month) && month >= 1 && month <= 12 ? month : fallbackMonth,
    };
  };

  const defaultStart = getYearMonth(startDate, minYear, 1);
  const defaultEnd = getYearMonth(endDate, currentYear, currentMonth);
  const defaultStartYear = defaultStart.year;
  const defaultStartMonth = defaultStart.month;
  const defaultEndYear = defaultEnd.year;
  const defaultEndMonth = defaultEnd.month;

  const [startYear, setstartYear] = React.useState(defaultStartYear);
  const [endYear, setendYear] = React.useState(defaultEndYear || currentYear);
  const [startMonth, setstartMonth] = React.useState(defaultStartMonth);
  const [endMonth, setendMonth] = React.useState(defaultEndMonth || currentMonth);

  React.useEffect(() => {
    if (fromDate != null) {
      const start = new Date(fromDate);
      setstartYear(start.getFullYear());
      setstartMonth(start.getMonth() + 1);
    } else {
      setstartYear(defaultStartYear);
      setstartMonth(defaultStartMonth);
    }

    if (toDate != null) {
      const end = new Date(toDate);
      setendYear(end.getFullYear());
      setendMonth(end.getMonth() + 1);
    } else {
      setendYear(defaultEndYear || currentYear);
      setendMonth(defaultEndMonth || currentMonth);
    }
  }, [fromDate, toDate, defaultStartYear, defaultStartMonth, defaultEndYear, defaultEndMonth, currentYear, currentMonth]);
  const handleChange = (event: any) => {
    setstartYear(event.target.value);
    const startObject = new Date(event.target.value, startMonth - 1);
    const endObject = new Date(endYear, endMonth - 1);
    onChange([startObject.valueOf(), endObject.valueOf()]);
  };
  const handleChange2 = (event: any) => {
    setendYear(event.target.value);
    const startObject = new Date(startYear, startMonth - 1);
    const endObject = new Date(event.target.value, endMonth - 1);
    onChange([startObject.valueOf(), endObject.valueOf()]);
  };
  const handleChange3 = (event: any) => {
    setstartMonth(event.target.value);
    const startObject = new Date(startYear, event.target.value - 1);
    const endObject = new Date(endYear, endMonth - 1);
    onChange([startObject.valueOf(), endObject.valueOf()]);
  };
  const handleChange4 = (event: any) => {
    setendMonth(event.target.value);
    const startObject = new Date(startYear, startMonth - 1);
    const endObject = new Date(endYear, event.target.value - 1);
    onChange([startObject.valueOf(), endObject.valueOf()]);
  };

  const generateYears = (startYear: number) => {
    const years = [];
    for (var i = startYear; i <= new Date().getFullYear(); i++) {
      years.push(i);
    }
    return years;
  };
  const generateMonths = () => {
    const months = [];
    for (var i = 1; i <= 12; i++) {
      months.push(i);
    }
    return months;
  };

  const yearOptions = generateYears(minYear);
  const monthOptions = generateMonths();

  return (
    <FilterListItem title={title} tooltip={tooltip} selected={0} open={open} setOpen={setOpen} noWrap={noWrap}>
      <Grid container spacing={4}>
        <Grid>
          <StyledFormControl variant="filled" style={{ width: "90px" }}>
            <InputLabel id="startYear">Start Year</InputLabel>
            <Select labelId="startYear" id={`${name}-fromYear`} value={startYear} onChange={handleChange}>
              {yearOptions.map(option => (
                <MenuItem key={`m1${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
        <Grid>
          <StyledFormControl variant="filled" style={{ width: "60px" }}>
            <InputLabel id="startMonth">Month</InputLabel>
            <Select labelId="startMonth" id={`${name}-fromMonth`} value={startMonth} onChange={handleChange3}>
              {monthOptions.map(option => (
                <MenuItem key={`m1${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
        <Grid>
          <StyledFormControl variant="filled" style={{ width: "90px" }}>
            <InputLabel id="endYear">End Year</InputLabel>
            <Select labelId="endYear" id={`${name}-toYear`} value={endYear} onChange={handleChange2}>
              {yearOptions.map(option => (
                <MenuItem key={`m2${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
        <Grid>
          <StyledFormControl variant="filled" style={{ width: "60px" }}>
            <InputLabel id="endMonth">Month</InputLabel>
            <Select labelId="endMonth" id={`${name}-toMonth`} value={endMonth} onChange={handleChange4}>
              {monthOptions.map(option => (
                <MenuItem key={`m2${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Grid>
      </Grid>

      <Box paddingTop={5}>
        <Button
          id={`${name}-clear`}
          variant="outlined"
          size="small"
          disabled={disabled || (fromDate === null && toDate === null)}
          onClick={() => {
            onChange([null, null]);
          }}
        >
          Clear
        </Button>
      </Box>
    </FilterListItem>
  );
};

export default MonthRangeFilter;
